/**
 * Analytics Initialization
 * Pre-emptively detects ad blockers and configures analytics accordingly
 */

import posthog from 'posthog-js';
import { checkPostHogBlocked, markPostHogAsBlocked } from './analytics-proxy';

let initialized = false;

/**
 * Set environment property for feature flag evaluation
 * Reusable function to ensure DRY - called from both init and identifyUser
 */
export function setEnvironmentForFeatureFlags(config: any): void {
    if (typeof window === 'undefined' || !posthog) {
        return;
    }

    const currentEnv = config?.env || 'dev';

    // Set person properties FOR FEATURE FLAGS evaluation (client-side)
    posthog.setPersonPropertiesForFlags({
        environment: currentEnv,
    });

    // Set as actual person properties (persisted to server)
    posthog.setPersonProperties({
        environment: currentEnv,
        app_name: 'pair',
    });

    // Reload feature flags with the new properties
    posthog.reloadFeatureFlags();

    console.debug(
        `[Analytics] Environment set for feature flags: ${currentEnv}`
    );
}

/**
 * Initialize analytics with smart detection (NON-BLOCKING)
 */
export function initializeAnalytics(config: any): void {
    if (typeof window === 'undefined') {
        return;
    }

    // Check if PostHog is already initialized
    if (initialized || (posthog && (posthog as any)._initializedAt)) {
        console.log('[Analytics Init] Already initialized, skipping');
        return;
    }

    initialized = true;

    // Check if analytics is blocked by our intercept script
    if ((window as any).__ANALYTICS_BLOCKED__) {
        console.log(
            '[Analytics Init] Analytics is blocked by intercept script, skipping PostHog initialization'
        );
        return;
    }

    // REMOVED blocking check - we want PostHog to initialize with our proxy
    // The proxy will handle bypassing ad blockers
    // const isBlocked = checkPostHogBlocked();
    //
    // if (isBlocked) {
    //     console.log(
    //         '[Analytics Init] PostHog is blocked (from proxy check), using proxy-only mode'
    //     );
    //     return;
    // }

    console.log(
        '[Analytics Init] PostHog not blocked, proceeding with initialization...'
    );
    console.log('[Analytics Init] Config check:', {
        hasConfig: !!config,
        hasKey: !!config?.posthog_key,
        keyPrefix: config?.posthog_key?.substring(0, 10),
    });

    // Only initialize PostHog if it's not blocked
    if (config?.posthog_key) {
        console.log(
            '[Analytics Init] Initializing PostHog with proxy at /api/posthog-proxy'
        );

        try {
            posthog.init(config.posthog_key, {
                // Use our telemetry endpoint to bypass ad blockers
                // Named "telemetry" to avoid detection
                api_host: '/api/telemetry',
                ui_host: 'https://us.posthog.com',
                capture_pageview: true, // Enable automatic page view tracking for better session context
                capture_pageleave: true, // Track when users leave pages
                autocapture: true, // Automatically capture user interactions (clicks, form submissions, etc.)
                disable_session_recording: false, // Enable session recordings
                enable_heatmaps: true, // Enable heatmap data capture for click/scroll/movement tracking
                session_recording: {
                    // Don't mask inputs - we want to see what users type
                    maskAllInputs: false,
                    maskTextSelector: '*[data-sensitive]', // Only mask elements explicitly marked as sensitive
                },
                // Session configuration - end session after 15 minutes of inactivity (reduced from default 30)
                session_idle_timeout_seconds: 900, // 15 minutes
                persistence: 'localStorage',
                request_batching: false, // Send immediately, don't batch
                // IMPORTANT: Disable ALL retries to prevent console flooding
                // Our proxy handles the bypass, we don't need retries
                capture_performance: true, // Enable performance tracking to understand issues
                // Note: Exception autocapture is enabled in PostHog project settings, not here
                // We use posthog.captureException() manually in ErrorBoundary for better control
                bootstrap: {
                    distinctID:
                        localStorage.getItem('pair_user_id') || undefined,
                },
                // Handle errors gracefully but DON'T disable PostHog when using proxy
                on_xhr_error: function (failedRequest: any) {
                    console.log(
                        '[Analytics Init] PostHog request failed, but continuing with proxy'
                    );

                    // Don't mark as blocked or disable PostHog when using proxy
                    // The proxy should handle the bypass

                    // Increment error counter for monitoring
                    (window as any).__posthog_errors =
                        ((window as any).__posthog_errors || 0) + 1;

                    // Return false to stop retries but keep PostHog active
                    return false;
                },
                loaded: (posthog) => {
                    console.log('[Analytics Init] PostHog loaded successfully');

                    const currentEnv = config.env || 'dev';
                    console.log(
                        '[Analytics Init] Setting environment for feature flags:',
                        currentEnv,
                        '(from config.env:',
                        config.env,
                        ')'
                    );

                    // Set super properties for EVENTS
                    posthog.register({
                        environment: currentEnv,
                        app_name: 'pair',
                        app_version:
                            (process.env.NEXT_PUBLIC_APP_VERSION as string) ||
                            process.env.npm_package_version ||
                            '1.0.0',
                        domain: window.location.hostname,
                    });

                    // CRITICAL: Set environment for feature flags
                    // Uses shared function for DRY - same logic in identifyUser()
                    setEnvironmentForFeatureFlags(config);

                    // START SESSION RECORDING FOR ALL USERS (authenticated and unauthenticated)
                    // This is crucial for understanding auth issues
                    try {
                        if (!(posthog as any).isSessionRecordingEnabled?.()) {
                            posthog.startSessionRecording();
                            console.log(
                                '[Analytics Init] Session recording started for all users'
                            );
                        }
                    } catch (error) {
                        console.error(
                            '[Analytics Init] Failed to start session recording:',
                            error
                        );
                    }

                    // Identify stored user if exists
                    const storedUserId = localStorage.getItem('pair_user_id');
                    const storedUserEmail =
                        localStorage.getItem('pair_user_email');
                    if (storedUserId) {
                        posthog.identify(storedUserId, {
                            email: storedUserEmail,
                        });
                    }

                    // DON'T enable debug mode to reduce console noise
                    // if (config.env === 'dev') {
                    //     posthog.debug();
                    // }
                },
            });
        } catch (error) {
            console.error(
                '[Analytics Init] Failed to initialize PostHog:',
                error
            );
        }
    }
}

/**
 * Check if analytics should use proxy
 */
export function shouldUseProxy(): boolean {
    // Check session storage for cached blocking status
    if (typeof window !== 'undefined' && window.sessionStorage) {
        const cachedStatus = sessionStorage.getItem('posthog_blocked_status');
        if (cachedStatus === 'true') {
            return true;
        }
    }

    // If PostHog is not initialized or window is undefined, use proxy
    if (typeof window === 'undefined' || !posthog || !posthog.capture) {
        return true;
    }

    // Check if PostHog has been failing
    const posthogErrors = (window as any).__posthog_errors || 0;
    if (posthogErrors > 2) {
        // Also cache this in session storage
        if (typeof window !== 'undefined' && window.sessionStorage) {
            sessionStorage.setItem('posthog_blocked_status', 'true');
        }
        return true;
    }

    return false;
}

// Track PostHog errors globally and suppress console noise
if (typeof window !== 'undefined') {
    (window as any).__posthog_errors = 0;

    // Intercept console.log to suppress $snapshot messages
    const originalConsoleLog = console.log;
    console.log = function (...args) {
        const logString = args.join(' ');

        // Suppress $snapshot messages
        if (
            logString.includes('$snapshot') ||
            logString.includes('$heatmap') ||
            logString.includes('snapshot') ||
            logString.includes('$autocapture') ||
            logString.includes('$web_vitals')
        ) {
            return; // Silently suppress
        }

        // Suppress retry messages
        if (
            logString.includes('Enqueued failed request for retry') ||
            logString.includes('Request blocked, using fallback') ||
            logString.includes('PostHog Intercept')
        ) {
            return; // Silently suppress
        }

        originalConsoleLog.apply(console, args);
    };

    // Intercept PostHog and browser extension errors
    const originalConsoleError = console.error;
    console.error = function (...args) {
        const errorString = args.join(' ');

        // Suppress browser extension background errors silently
        if (
            errorString.includes('background/carbon') ||
            errorString.includes('background/framework') ||
            errorString.includes('background-carbon') ||
            errorString.includes('background-framework') ||
            errorString.includes('background-legacy') ||
            errorString.includes('getUserLoginStatus') ||
            errorString.includes('getGlobalExtensionSettings') ||
            errorString.includes('CarbonApi')
        ) {
            return; // Silently suppress without logging
        }

        // Track PostHog errors but don't log them to avoid flooding
        if (
            errorString.includes('posthog') ||
            errorString.includes('PostHog') ||
            errorString.includes('/api/telemetry') ||
            errorString.includes('telemetry') ||
            errorString.includes('Failed to fetch')
        ) {
            (window as any).__posthog_errors =
                ((window as any).__posthog_errors || 0) + 1;
            return; // Suppress to avoid flooding console
        }

        originalConsoleError.apply(console, args);
    };

    // Also intercept console.warn for PostHog warnings
    const originalConsoleWarn = console.warn;
    console.warn = function (...args) {
        const warnString = args.join(' ');

        // Suppress PostHog warnings
        if (
            warnString.includes('PostHog') ||
            warnString.includes('posthog') ||
            warnString.includes('telemetry') ||
            warnString.includes('Enqueued failed request')
        ) {
            return; // Silently suppress
        }

        originalConsoleWarn.apply(console, args);
    };

    // ========================================
    // GLOBAL ERROR HANDLERS FOR POSTHOG
    // Capture ALL JavaScript errors, not just React errors
    // ========================================

    /**
     * Check if an error should be suppressed (known/expected errors)
     */
    const shouldSuppressError = (message: string): boolean => {
        const suppressedPatterns = [
            // Browser extension errors
            'background/carbon',
            'background/framework',
            'background-carbon',
            'background-framework',
            'background-legacy',
            'getUserLoginStatus',
            'getGlobalExtensionSettings',
            'CarbonApi',
            // PostHog internal errors (already tracked separately)
            'posthog',
            'PostHog',
            '/api/telemetry',
            // MetaMask/Web3 errors
            'MetaMask',
            'ethereum',
            'web3',
            // Expected errors
            'ResizeObserver loop',
            'Script error',
            'Loading chunk',
        ];

        return suppressedPatterns.some((pattern) =>
            message.toLowerCase().includes(pattern.toLowerCase())
        );
    };

    /**
     * Send exception to PostHog
     */
    const captureExceptionToPostHog = (
        message: string,
        source?: string,
        lineno?: number,
        colno?: number,
        error?: Error,
        type: string = 'Error'
    ): void => {
        // Don't capture if PostHog isn't ready
        if (!posthog || typeof posthog.capture !== 'function') {
            return;
        }

        // Don't capture suppressed errors
        if (shouldSuppressError(message)) {
            return;
        }

        try {
            posthog.capture('$exception', {
                $exception_message: message,
                $exception_type: type,
                $exception_source: source || 'unknown',
                $exception_lineno: lineno,
                $exception_colno: colno,
                $exception_stack: error?.stack,
                $current_url: window.location.href,
                $pathname: window.location.pathname,
                $host: window.location.host,
                $timestamp: new Date().toISOString(),
                // Additional context
                user_agent: navigator.userAgent,
                viewport_width: window.innerWidth,
                viewport_height: window.innerHeight,
            });
        } catch (captureError) {
            // Silently fail - don't create infinite loops
        }
    };

    // Global error handler for synchronous errors
    const previousOnError = window.onerror;
    window.onerror = function (
        message: string | Event,
        source?: string,
        lineno?: number,
        colno?: number,
        error?: Error
    ): boolean {
        const errorMessage =
            typeof message === 'string'
                ? message
                : message?.toString() || 'Unknown error';

        captureExceptionToPostHog(
            errorMessage,
            source,
            lineno,
            colno,
            error,
            error?.name || 'Error'
        );

        // Call previous handler if it exists
        if (previousOnError) {
            return previousOnError.call(
                window,
                message,
                source,
                lineno,
                colno,
                error
            );
        }

        // Return false to allow default browser error handling
        return false;
    };

    // Global handler for unhandled Promise rejections
    const previousOnUnhandledRejection = window.onunhandledrejection;
    window.onunhandledrejection = function (
        event: PromiseRejectionEvent
    ): void {
        const reason = event.reason;
        const message =
            reason?.message ||
            (typeof reason === 'string'
                ? reason
                : 'Unhandled Promise Rejection');
        const stack = reason?.stack;

        captureExceptionToPostHog(
            message,
            'promise',
            undefined,
            undefined,
            reason instanceof Error ? reason : undefined,
            'UnhandledPromiseRejection'
        );

        // Call previous handler if it exists
        if (previousOnUnhandledRejection) {
            previousOnUnhandledRejection.call(window, event);
        }
    };
}
